import {
  CompQuestionTemplateType,
  LiveScoreProvider,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';

export const isSupported = (
  provider: LiveScoreProvider | undefined,
  templateType: CompQuestionTemplateType,
) => {
  if (!provider || templateType === CompQuestionTemplateType.DEFAULT) {
    return true;
  }

  const unsupported: Record<LiveScoreProvider, CompQuestionTemplateType[]> = {
    [LiveScoreProvider.FLASHSCORE]: Object.values(CompQuestionTemplateType),
    [LiveScoreProvider.GENIUS]: [
      CompQuestionTemplateType.HALF_SCORES,
      CompQuestionTemplateType.HALF_SCORES_DRAW,
      CompQuestionTemplateType.HALF_SCORES_HOME,
      CompQuestionTemplateType.HALF_SCORES_AWAY,
      CompQuestionTemplateType.HALF_SCORES_HOME_DRAW,
      CompQuestionTemplateType.HALF_SCORES_AWAY_DRAW,
    ],
    [LiveScoreProvider.GOOGLE]: Object.values(CompQuestionTemplateType),
    [LiveScoreProvider.RADAR]: [
      CompQuestionTemplateType.TEAM_GOAL_SCORER,
      CompQuestionTemplateType.AVERAGE_HIGH_SCORER,
      CompQuestionTemplateType.LAST_HIGH_SCORER,
      CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.AWAY_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.FAN_MVP,
      CompQuestionTemplateType.HALF_SCORES,
      CompQuestionTemplateType.HALF_SCORES_DRAW,
      CompQuestionTemplateType.HALF_SCORES_HOME,
      CompQuestionTemplateType.HALF_SCORES_AWAY,
      CompQuestionTemplateType.HALF_SCORES_HOME_DRAW,
      CompQuestionTemplateType.HALF_SCORES_AWAY_DRAW,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
    ],
    [LiveScoreProvider.PULSELIVE]: [
      CompQuestionTemplateType.TEAM_GOAL_SCORER,
      CompQuestionTemplateType.AVERAGE_HIGH_SCORER,
      CompQuestionTemplateType.LAST_HIGH_SCORER,
      CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.AWAY_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.FAN_MVP,
      CompQuestionTemplateType.HOME_HIGH_SCORER,
      CompQuestionTemplateType.AWAY_HIGH_SCORER,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
    ],
    [LiveScoreProvider.SOFASCORE]: [
      CompQuestionTemplateType.TEAM_GOAL_SCORER,
      CompQuestionTemplateType.AVERAGE_HIGH_SCORER,
      CompQuestionTemplateType.LAST_HIGH_SCORER,
      CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.AWAY_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.FAN_MVP,
      CompQuestionTemplateType.HOME_HIGH_SCORER,
      CompQuestionTemplateType.AWAY_HIGH_SCORER,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
    ],
    [LiveScoreProvider.TABNZ]: [
      CompQuestionTemplateType.TEAM_GOAL_SCORER,
      CompQuestionTemplateType.AVERAGE_HIGH_SCORER,
      CompQuestionTemplateType.LAST_HIGH_SCORER,
      CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.AWAY_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.FAN_MVP,
      CompQuestionTemplateType.HOME_HIGH_SCORER,
      CompQuestionTemplateType.AWAY_HIGH_SCORER,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
    ],
    // The rugby codes support everything except the football-only scorer type.
    [LiveScoreProvider.NRL]: [CompQuestionTemplateType.TEAM_GOAL_SCORER],
    [LiveScoreProvider.RUGBYPASS]: [CompQuestionTemplateType.TEAM_GOAL_SCORER],
    /**
     * ESPN carries a full per-player line, so the scorer families work — but
     * football has no tries, and FAN_MVP has no football stat to rank on.
     */
    [LiveScoreProvider.ESPN]: [
      CompQuestionTemplateType.FAN_MVP,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
    ],
    [LiveScoreProvider.WORLD_RUGBY]: [
      CompQuestionTemplateType.TEAM_GOAL_SCORER,
      CompQuestionTemplateType.AVERAGE_HIGH_SCORER,
      CompQuestionTemplateType.LAST_HIGH_SCORER,
      CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.AWAY_ABOVE_AVERAGE_SCORERS,
      CompQuestionTemplateType.FAN_MVP,
      CompQuestionTemplateType.HOME_HIGH_SCORER,
      CompQuestionTemplateType.AWAY_HIGH_SCORER,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
    ],
    // EUI provides full basketball box scores — same capabilities as GENIUS.
    [LiveScoreProvider.SPORTRADAR_EUI]: [
      CompQuestionTemplateType.HALF_SCORES,
      CompQuestionTemplateType.HALF_SCORES_DRAW,
      CompQuestionTemplateType.HALF_SCORES_HOME,
      CompQuestionTemplateType.HALF_SCORES_AWAY,
      CompQuestionTemplateType.HALF_SCORES_HOME_DRAW,
      CompQuestionTemplateType.HALF_SCORES_AWAY_DRAW,
      CompQuestionTemplateType.HOME_TRY_SCORER,
      CompQuestionTemplateType.AWAY_TRY_SCORER,
      CompQuestionTemplateType.TEAM_TRY_SCORER,
      CompQuestionTemplateType.TEAM_GOAL_SCORER,
    ],
  };

  return !unsupported[provider].includes(templateType);
};
